import {
  type Bundle,
  type OrderV1IncludesAll as OrderIncludesAll,
  type PairingV1 as Pairing,
} from '@hungryroot/rest';
import { type Order as BundledOrder } from '../../api/v2/customers/[id]/orders/[order_id]/get/get-customer-order';
import { pairingIsModified } from '../../components/cart/query';
import { convertOrderIngredientToCartProduct } from '../cart/transforms';

interface PairingWithOrderDetails extends Pairing {
  order_details: Bundle['order_details'];
  is_modified: boolean;
}

interface OrderWithPairingBundles extends OrderIncludesAll {
  pairings: PairingWithOrderDetails[];
}

export function convertBundledOrderToOrder(
  bundledOrder: BundledOrder,
  order: OrderIncludesAll
): OrderWithPairingBundles {
  const {
    bundles,
    credit,
    credit_delta,
    customer_name,
    cutoff_a_delivery_dates,
    cutoff_b_delivery_dates,
    cutoff_date,
    day_of_week,
    days_left_to_customize,
    discount,
    grouping_status,
    has_auto_refunded,
    id,
    in_seven_day_period,
    is_customized,
    is_editable,
    net_revenue,
    possible_cutoffs,
    points,
    promo_code_id,
    quantity,
    rating,
    rating_cutoff_date,
    rollover_credits,
    serving_size,
    sequence,
    shipping_cost,
    show_badge,
    sku_inventory_limits,
    status_label,
    subtotal,
    tax,
    total,
    tracking_url,
    preferred_date,
    status,
  } = bundledOrder;

  const { grocery_items, pairings, occasions } = order;

  const orderPairings: PairingWithOrderDetails[] = [];

  bundles.forEach((bundle) => {
    const { pairing, order_details } = bundle;
    if (pairing) {
      let pairingIngredients = pairings
        .find((orderPairing) => orderPairing.id === pairing.id)
        ?.ingredients.map((ing) => convertOrderIngredientToCartProduct(ing));

      if (pairingIngredients === undefined) {
        if (process.env.NODE_ENV !== 'production') {
          throw new Error(
            'Unable to convert cart pairing to order pairing, not all ingredients are present'
          );
        }
        pairingIngredients = [];
      }
      orderPairings.push({
        ...pairing,
        dish_type: {
          ...pairing.dish_type,
          sort_order: 0,
        },
        tags: pairing.tags.map(({ id }) => id),
        // TODO: udate to pairing.featured_img when BE fixes missing field
        featured_img: pairing.featured_img_url,
        order_details: order_details,
        is_modified: pairingIsModified(
          order_details,
          pairing.needed_ingredients
        ),
        // TODO: remove when BE fixes missing ingredients fields
        ingredients:
          pairings.find((orderPairing) => orderPairing.id === pairing.id)
            ?.ingredients || [],
        is_favorited: false,
        points: parseFloat(pairing.points),
      });
    }
  });

  return {
    credit: credit,
    credit_delta: credit_delta,
    customer_name: customer_name,
    cutoff_a_delivery_dates: cutoff_a_delivery_dates,
    cutoff_b_delivery_dates: cutoff_b_delivery_dates,
    cutoff_date: cutoff_date,
    day_of_week: day_of_week,
    days_left_to_customize: days_left_to_customize,
    discount: discount,
    grocery_items: grocery_items,
    grouping_status: grouping_status,
    has_auto_refunded: has_auto_refunded,
    has_bundles: true,
    id: id,
    in_seven_day_period: in_seven_day_period,
    is_customized: is_customized,
    is_editable: is_editable,
    net_revenue: net_revenue,
    occasions: occasions,
    pairings: orderPairings,
    points: points,
    possible_cutoffs: possible_cutoffs,
    preferred_date: preferred_date,
    promo_code: null,
    promo_codes: [],
    promo_code_id,
    cart_save_threshold: '0',
    quantity: quantity,
    rating: rating,
    rating_cutoff_date: rating_cutoff_date,
    rollover_credits: rollover_credits,
    sequence: sequence,
    serving_size: serving_size,
    shipping_cost: shipping_cost,
    show_badge: show_badge,
    sku_inventory_limits: sku_inventory_limits,
    status: status,
    status_label: status_label,
    subtotal: subtotal,
    tax: tax,
    tracking_url: tracking_url,
    total: total,
  };
}
